// Web bridge factory.
//
// Returns an object that satisfies the same shape as the
// `window.screenplay` contextBridge surface defined in
// src/main/preload.js, so the unchanged renderer (`src/renderer/js/app.js`)
// runs against either Electron IPC or the web bridge without knowing
// which environment it's in.
//
// Per-namespace modules contribute their slice of the surface; noop.js
// fills in anything not yet implemented so the bridge object stays
// shape-complete and the renderer's `if (window.screenplay.X)` guards
// keep working.

import { createEventBus } from './events.js';
import { buildNoopHandlers } from './noop.js';
import { buildFileHandlers } from './file.js';
import { buildDraftsHandlers } from './drafts.js';
import { buildRecentHandlers } from './recent.js';
import { buildAuthHandlers } from './auth.js';
import { buildSubscriptionHandlers } from './subscription.js';
import { buildEnvHandlers, getPublicEnv } from './env.js';
import { buildAIHandlers } from './ai.js';
import { buildHistoryHandlers } from './history.js';
import { buildMenuHandlers } from './menu.js';
import { buildBillingHandlers } from './billing.js';
import { buildPdfHandlers } from './pdf.js';
import { buildMigrateHandlers } from './migrate.js';
import { buildImportHandlers } from './import.js';
import { buildCloudHandlers } from './cloud.js';
import { buildYjsCacheHandlers } from './yjs-cache.js';
import { buildNotesHandlers } from './notes.js';
import { installPrefsSync } from './prefs.js';
import { installMultiTab } from './multi-tab.js';
import { getSupabase } from '../supabase/client.js';

export function createBridge() {
  const bus = createEventBus();
  const supabase = getSupabase();
  const env = getPublicEnv();

  // Side-effect installers (no exported handlers — they hook the
  // global keydown listener / supabase auth events / BroadcastChannel
  // directly).
  buildMenuHandlers({ bus });
  installPrefsSync({ supabase });
  installMultiTab();

  // Build migrate first so file's backupListCloud can call its
  // ensureElectronImportOnce hook on the very first welcome list.
  const migrateHandlers = buildMigrateHandlers({ supabase, env });

  const handlers = {
    ...buildNoopHandlers(),
    ...buildFileHandlers({
      supabase,
      bus,
      runPrelistMigration: migrateHandlers.ensureElectronImportOnce,
    }),
    ...buildDraftsHandlers({ supabase }),
    ...buildRecentHandlers({ supabase }),
    ...buildAuthHandlers({ supabase, bus }),
    ...buildSubscriptionHandlers({ supabase }),
    ...buildEnvHandlers(),
    ...buildAIHandlers({ supabase, env }),
    ...buildHistoryHandlers({ supabase }),
    ...buildBillingHandlers({ supabase, env }),
    ...buildPdfHandlers({ supabase, env }),
    ...migrateHandlers,
    ...buildImportHandlers({ supabase, bus }),
    ...buildCloudHandlers({ supabase, env, bus }),
    ...buildYjsCacheHandlers(),
    ...buildNotesHandlers({ supabase, env }),
  };

  const subscribeTo = (channel) => (cb) => bus.on(channel, cb);

  const MENU_CHANNELS = [
    'menu:new',
    'menu:new-from-template',
    'menu:close',
    'menu:save',
    'menu:save-as',
    'menu:save-copy-to-mac',
    'menu:export-fountain',
    'menu:export-fdx',
    'menu:export-fdx-no-notes',
    'menu:export-celtx',
    'menu:export-docx',
    'menu:export-rtf',
    'menu:export-text',
    'menu:export-html',
    'menu:export-csv',
    'menu:export-json',
    'menu:export-pdf',
    'menu:find',
    'menu:title-page',
    'menu:backup',
    'menu:revert',
    'menu:print',
    'menu:print-preview',
    'menu:toggle-ai',
    'menu:toggle-sidebar',
    'menu:toggle-timeline',
    'menu:toggle-notes',
    'menu:settings',
    'menu:open-cloud',
    'menu:check-for-updates',
  ];

  return {
    ...handlers,
    onScriptLoaded: subscribeTo('script:loaded'),
    onFolderLoaded: subscribeTo('folder:loaded'),
    onBackupStatus: subscribeTo('backup:status'),
    onUpdateStatus: subscribeTo('update:status'),
    onAuthCallbackComplete: subscribeTo('auth:callback-complete'),
    onTestPleaseSignalWhenRendered: subscribeTo('test:please-signal-when-rendered'),
    onTestPleaseSerialize: subscribeTo('test:please-serialize'),
    // The Hocuspocus graceful-quit drain handshake never fires on web
    // (no quit lifecycle) but the renderer subscribes once at init.
    onCloudDrainRequest: subscribeTo('cloud:drain-request'),
    // The PDF parser asks for a password via this channel; web shows
    // an in-app modal in response. The renderer wires the listener;
    // the bridge just provides the bus channel.
    onPdfPasswordRequest: subscribeTo('pdf-password:request'),
    onMenuAction: (cb) => {
      const offs = MENU_CHANNELS.map((ch) => bus.on(ch, () => cb(ch)));
      return () => offs.forEach((off) => off());
    },
    __bus: bus,
    __supabase: supabase,
  };
}
